import { useEffect, useState } from "react";
import { useAppDispatch } from "../../../../redux/hooks";
import {
  QuarantinedProductsToReturnStockInterface,
  ReturningProductsType,
} from "../../../../redux/interfaces/inventory.interface";
import { postProdToReturnFromReception } from "../../../../redux/api/receipt.api";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../../helpers/apiHelper";

import CustomButton from "../../../../components/CustomButton";
import ModalWrapper from "../../../../components/Modal/ModalWrapper";
import TextInput from "../../../../components/misc/TextInput";
import Text from "../../../../styleguide/Text";

import Dropdown, { DropdownValue } from "../../../../components/misc/Dropdown";
import ContentCard from "../../../../components/misc/ContentCard";
import { returningProductTypes } from "../../../../constants/general";

interface InvoiceDropdownValue extends DropdownValue {
  idFactura: number;
  codFactura: string;
}

const ReturningProductsModal = ({
  selectedReturningProducts,
  setSelectedItems,
}: {
  selectedReturningProducts: QuarantinedProductsToReturnStockInterface[];
  setSelectedItems: any;
}) => {
  const dispatch = useAppDispatch();

  const [isReturningModalOpen, setIsReturningModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [returningProducts, setReturningProducts] = useState<
    ReturningProductsType[]
  >([]);
  const [returnDocNrAndDate, setReturnDocNrAndDate] = useState<
    string | number | null
  >(null);
  const [selectedDocType, setSelectedDocType] = useState<DropdownValue | null>(
    null,
  );
  const [providersData, setProvidersData] = useState<DropdownValue[]>([]);
  const [selectedProvider, setSelectedProvider] =
    useState<DropdownValue | null>(null);
  const [invoicesData, setInvoicesData] = useState<InvoiceDropdownValue[]>([]);
  const [selectedInvoice, setSelectedInvoice] =
    useState<InvoiceDropdownValue | null>(null);

  useEffect(() => {
    if (isReturningModalOpen && selectedReturningProducts.length) {
      const allProviders = selectedReturningProducts.flatMap(
        (item) => item.furnizori,
      );
      const providers = allProviders.filter(
        (provider, index, self) =>
          self.findIndex((item) => item.id === provider.id) === index,
      );

      setReturningProducts(selectedReturningProducts);
      setProvidersData(providers);
      setSelectedProvider(providers[0] || null);
    }
  }, [isReturningModalOpen, selectedReturningProducts]);

  useEffect(() => {
    if (!selectedProvider?.id) {
      setInvoicesData([]);
      setSelectedInvoice(null);
      return;
    }

    const allInvoices = selectedReturningProducts
      .flatMap((product) => product.furnizori)
      .filter((provider) => provider.id === selectedProvider.id)
      .flatMap((provider) => provider.facturi || []);
    const invoices = allInvoices
      .filter(
        (invoice, index, self) =>
          self.findIndex((item) => item.idFactura === invoice.idFactura) ===
          index,
      )
      .map((invoice) => ({
        id: invoice.idFactura,
        label: invoice.codFactura,
        idFactura: invoice.idFactura,
        codFactura: invoice.codFactura,
      }));

    setInvoicesData(invoices);
    setSelectedInvoice(invoices[0] || null);
  }, [selectedProvider, selectedReturningProducts]);

  useEffect(() => {
    if (!isReturningModalOpen) {
      setReturningProducts([]);
      setReturnDocNrAndDate(null);
      setSelectedDocType(null);
      setSelectedProvider(null);
      setInvoicesData([]);
      setSelectedInvoice(null);
    }
  }, [isReturningModalOpen]);

  const handleReturningProductsModalShow = (show: boolean) => {
    setIsReturningModalOpen(show || false);
    return;
  };

  const handleChangeProductQuantity = (
    id: string,
    value: string | number | null,
  ) => {
    setReturningProducts((prev) => {
      return prev.map((product) => {
        if (id === product.id) {
          if (Number(value) <= product.cantitate && Number(value) >= 0) {
            return {
              ...product,
              quantityToReturn: value ? Number(value) : null,
            };
          }
        }
        return product;
      });
    });
  };

  const handleChangeProductReturningDocType = (
    id: string | number,
    value: string | number | null,
  ) => {
    if (value) {
      setSelectedDocType({
        id: id,
        label: value,
      });
    }
  };

  const handleChangeProductReturningProvider = (
    id: string | number,
    value: string | number | null,
  ) => {
    if (value) {
      setSelectedProvider({
        id: id,
        label: value,
      });
    }
  };

  const handleChangeProductReturningInvoice = (
    value: InvoiceDropdownValue | null,
  ) => {
    setSelectedInvoice(value);
  };

  const filteredReturningProducts = returningProducts.filter((product) =>
    product.furnizori.some(
      (provider) =>
        provider.id === selectedProvider?.id &&
        provider.facturi?.some(
          (invoice) => invoice.idFactura === selectedInvoice?.idFactura,
        ),
    ),
  );

  const handleSaveReturningProducts = async () => {
    if (!selectedDocType?.id || !selectedProvider?.id || !selectedInvoice?.id) {
      return;
    }
    setIsLoading(true);

    const resultAction = await dispatch(
      postProdToReturnFromReception({
        productsToReturn: filteredReturningProducts,
        page: 1,
        perPage: 9999,
        docType: selectedDocType?.id,
        provider: selectedProvider?.id,
        invoice: {
          idFactura: selectedInvoice.idFactura,
          codFactura: selectedInvoice.codFactura,
        },
        docNrAndDate: returnDocNrAndDate,
      }),
    );

    if (
      checkEndpointStatus(resultAction, postProdToReturnFromReception) !==
      endpointStatus.pending
    ) {
      setIsLoading(false);
    }

    if (
      checkEndpointStatus(resultAction, postProdToReturnFromReception) ===
      endpointStatus.fulfilled
    ) {
      setSelectedItems([]);
    }
  };

  const isSubmitDisabled =
    !returnDocNrAndDate ||
    !selectedInvoice?.id ||
    !filteredReturningProducts.length ||
    filteredReturningProducts?.some((product) => !product.quantityToReturn);

  const onChangeDocReturValue = (value: string | number | null) => {
    setReturnDocNrAndDate(value);
  };

  return (
    <>
      <CustomButton
        onClick={() => handleReturningProductsModalShow(true)}
        variant="contained">
        Returnare
      </CustomButton>
      <ModalWrapper
        title="Retur produse la furnizor"
        modalSize="xl"
        show={isReturningModalOpen}
        handleClose={() => handleReturningProductsModalShow(false)}>
        <Dropdown
          className="my-4"
          title="Catre furnizorul"
          data={providersData}
          singleValue={selectedProvider}
          setSingleValue={(value) => {
            if (value?.id) {
              handleChangeProductReturningProvider(value.id, value.label);
            }
          }}
        />
        <Dropdown
          className="my-4"
          title="Factura"
          data={invoicesData}
          singleValue={selectedInvoice}
          setSingleValue={(value) => {
            if (value?.id) {
              handleChangeProductReturningInvoice(
                value as InvoiceDropdownValue,
              );
            }
          }}
        />
        <Dropdown
          title="Tip doc retur"
          className="ml-2"
          data={returningProductTypes}
          singleValue={selectedDocType}
          setSingleValue={(value) => {
            if (value?.id) {
              handleChangeProductReturningDocType(value.id, value.label);
            }
          }}
        />

        <TextInput
          name="docRetur"
          value={returnDocNrAndDate}
          label="Nr/data doc retur"
          onChange={onChangeDocReturValue}
        />
        {filteredReturningProducts?.map((product) => {
          return (
            <ContentCard key={product.idActivCel}>
              <div className="flex flex-col">
                <div className="flex justify-between">
                  <Text
                    variant="h3"
                    component="h3"
                    className={`font-semibold text-slate-900 mb-4 mr-2`}
                    gutterBottom>
                    {product.numeProdusErp}
                  </Text>
                </div>
                <div className="flex justify-between mb-4">
                  <Text
                    variant="body1"
                    component="body1"
                    isBold
                    className={`font-semibold text-slate-900 mr-2`}>
                    {product.lot}
                  </Text>
                  <Text
                    variant="body1"
                    component="body1"
                    isBold
                    className={`font-semibold text-slate-900 mr-2`}>
                    {product.dataExpirare}
                  </Text>
                </div>

                <TextInput
                  type="number"
                  name={product.numeProdusErp}
                  label={"Cantitate"}
                  value={product.quantityToReturn}
                  placeholder="Cantitate de returnat"
                  onChange={(value) =>
                    handleChangeProductQuantity(product.id, value)
                  }
                  endComponent={
                    <Text
                      variant="body1"
                      color={"royalblue"}
                      className="rounded-control bg-white p-2">
                      Cantitate totala: {product.cantitate}
                    </Text>
                  }
                />
              </div>
            </ContentCard>
          );
        })}

        <CustomButton
          type="submit"
          variant="contained"
          fullWidth
          className="inline-flex items-center justify-center rounded-control border-0 px-4 py-2 font-semibold transition min-h-12 px-5 text-base bg-brand text-brand-foreground hover:brightness-95 w-full mt-5"
          disabled={isSubmitDisabled}
          loading={isLoading}
          onClick={handleSaveReturningProducts}>
          <Text variant="body1" className="" isBold>
            Salveaza
          </Text>
        </CustomButton>
      </ModalWrapper>
    </>
  );
};

export default ReturningProductsModal;
